<template>
  <div class="px-4 pb-4 flex flex-col justify-start items-start gap-2">
    <div class="self-stretch rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 flex flex-col justify-start items-start">
      <div class="self-stretch px-6 py-4 bg-zinc-800 border-b border-zinc-700 inline-flex justify-start items-center gap-4">
        <div class="flex-1 inline-flex flex-col justify-start items-start gap-2">
          <div class="self-stretch justify-center text-white text-xl font-bold font-['Roboto'] leading-6 tracking-tight">
            {{ businessName || 'Business Profile' }}
          </div>
        </div>
        <div class="flex justify-start items-start gap-4">
          <button v-if="can_edit_business_brand_profiles" @click="showEditProfile" data-hierarchy="Secondary gray" data-icon-only="False" data-show-leading-icon="true" data-show-trailing-icon="false" data-size="sm" data-state="Default" class="px-3.5 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-300 flex justify-center items-center gap-2 overflow-hidden">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.21667 5.01667L9.98333 5.78333L2.43333 13.3333H1.66667V12.5667L9.21667 5.01667ZM12.2167 0C12.0083 0 11.7917 0.0833333 11.6333 0.241667L10.1083 1.76667L13.2333 4.89167L14.7583 3.36667C15.0833 3.04167 15.0833 2.51667 14.7583 2.19167L12.8083 0.241667C12.6417 0.075 12.4333 0 12.2167 0ZM9.21667 2.65833L0 11.875V15H3.125L12.3417 5.78333L9.21667 2.65833Z" fill="#606060" />
            </svg>
            <div class="justify-start text-zinc-600 text-sm font-medium font-['Roboto'] leading-5">Edit profile</div>
          </button>
          <button v-if="can_edit_business_brand_profiles" @click="showWarning" data-hierarchy="Secondary" data-icon-only="True" data-show-leading-icon="true" data-show-trailing-icon="true" data-size="sm" data-state="Default" class="p-2 bg-white rounded-md shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] outline outline-1 outline-offset-[-1px] outline-red-600 flex justify-center items-center gap-2 overflow-hidden">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_18731_3525)">
                <path d="M13.3334 7.5V15.8333H6.66675V7.5H13.3334ZM12.0834 2.5H7.91675L7.08341 3.33333H4.16675V5H15.8334V3.33333H12.9167L12.0834 2.5ZM15.0001 5.83333H5.00008V15.8333C5.00008 16.75 5.75008 17.5 6.66675 17.5H13.3334C14.2501 17.5 15.0001 16.75 15.0001 15.8333V5.83333Z" fill="#DC2626" />
              </g>
              <defs>
                <clipPath id="clip0_18731_3525">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>
      </div>
      <div class="self-stretch p-6 inline-flex justify-start items-start gap-6">
        <div class="flex-1 inline-flex flex-col justify-start items-start gap-3">
          <div class="self-stretch justify-center text-white text-base font-bold font-['Roboto'] leading-5 tracking-tight">Branding</div>
          <div class="self-stretch h-16 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex justify-start items-center gap-6">
            <div class="flex-1 justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Primary Color</div>
            <div class="w-32 flex justify-start items-center gap-4">
              <div class="w-8 h-8 rounded-md border border-white" :style="'background:' + (branding?.primaryColor || '#FFFFFF')"></div>
              <div class="flex-1 justify-start text-white text-base font-normal font-['Roboto'] leading-6">
                {{ branding?.primaryColor || '#FFFFFF' }}
              </div>
            </div>
          </div>
          <div class="self-stretch h-16 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex justify-start items-center gap-6">
            <div class="flex-1 justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Secondary Color</div>
            <div class="w-32 flex justify-start items-center gap-4">
              <div class="w-8 h-8 rounded-md border border-white" :style="'background:' + (branding?.secondaryColor || '#FFFFFF')"></div>
              <div class="flex-1 justify-start text-white text-base font-normal font-['Roboto'] leading-6">
                {{ branding?.secondaryColor || '#FFFFFF' }}
              </div>
            </div>
          </div>
          <div class="self-stretch inline-flex justify-start items-start gap-3">
            <div class="flex-1 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex flex-col justify-center items-start gap-2">
              <div class="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Business Logo</div>
              <img v-if="branding?.logo" class="self-stretch h-14 relative rounded-md object-contain" :src="branding.logo" />
              <div v-else class="self-stretch h-14 relative rounded-md bg-zinc-700 flex items-center justify-center text-neutral-400 text-xs">No logo</div>
            </div>
            <div class="flex-1 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex flex-col justify-center items-start gap-2">
              <div class="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Browser Favicon</div>
              <img v-if="branding?.favicon" class="self-stretch h-14 relative rounded-md object-contain" :src="branding.favicon" />
              <div v-else class="self-stretch h-14 relative rounded-md bg-zinc-700 flex items-center justify-center text-neutral-400 text-xs">No favicon</div>
            </div>
          </div>
        </div>
        <div class="flex-1 inline-flex flex-col justify-start items-start gap-3">
          <div class="self-stretch justify-center text-white text-base font-bold font-['Roboto'] leading-5 tracking-tight">Website & Domain Settings</div>
          <div class="self-stretch h-16 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 flex flex-col justify-start items-start gap-0.5">
            <div class="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Google Analytics Tracking ID</div>
            <div class="self-stretch justify-start text-white text-base font-normal font-['Roboto'] leading-6">
              {{ googleAnalyticsTrackingId || 'Not set' }}
            </div>
          </div>
          <div class="self-stretch h-16 px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 flex flex-col justify-start items-start gap-0.5">
            <div class="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">Merchant Website URL</div>
            <div class="self-stretch justify-start text-white text-base font-normal font-['Roboto'] leading-6">
              {{ merchantWebsiteUrl || 'Not set' }}
            </div>
          </div>

          <div class="self-stretch w-full px-0 py-0">
            <label class="flex items-center cursor-pointer">
              <span class="text-base leading-5 tracking-[0.15px] h-[20px] mr-5 text-white">Cash Rounding</span>
              <input disabled type="checkbox" :checked="branding?.cashRounding?.cashRounding" class="hidden peer">
              <div :class="'relative toggle-bg border-2 h-6 w-11 rounded-full ' + (branding?.cashRounding?.cashRounding ? 'bg-theme-green border-theme-green' : 'bg-[#F53333] border-[#F53333]')"></div>
            </label>
            <p class="font-normal text-sm leading-[17.5px] my-3 text-neutral-300">With cash rounding enabled, your cash payments will be rounded to the nearest .05.</p>
          </div>

          <SeoSetting v-if="merchantWebsiteUrl" :domain="merchantWebsiteUrl" />
          
          <!-- <div class="self-stretch px-4 py-2 rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 flex flex-col justify-start items-start gap-0.5">
            <div class="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Roboto'] leading-5">DNS Record</div>
            <div class="self-stretch inline-flex justify-start items-center gap-2">
              <div class="justify-start text-neutral-400 text-base font-normal font-['Roboto'] leading-6 line-clamp-1">Link:</div>
              <div class="flex-1 justify-start text-white text-base font-normal font-['Roboto'] underline leading-6 line-clamp-1">text link</div>
            </div>
            <div class="self-stretch inline-flex justify-start items-center gap-2">
              <div class="justify-start text-neutral-400 text-base font-normal font-['Roboto'] leading-6 line-clamp-1">Type:</div>
              <div class="flex-1 justify-start text-white text-base font-normal font-['Roboto'] leading-6 line-clamp-1">CNAME</div>
            </div>
            <div class="self-stretch inline-flex justify-start items-center gap-2">
              <div class="justify-start text-neutral-400 text-base font-normal font-['Roboto'] leading-6 line-clamp-1">Value:</div>
              <div class="flex-1 justify-start text-white text-base font-normal font-['Roboto'] leading-6 line-clamp-1">bargainbuds.com</div>
            </div>
          </div> -->
        </div>
        <div class="flex-1 inline-flex flex-col justify-start items-start gap-3">
          <div class="self-stretch inline-flex justify-start items-center gap-2">
            <div class="flex-1 justify-center text-white text-base font-bold font-['Roboto'] leading-5 tracking-tight">Assigned Locations ({{ locations.length }})</div>
            <button v-if="can_edit_business_brand_profiles" @click="showManage" :disabled="brandingSize < 2" data-hierarchy="Link color" data-icon-only="False" data-show-leading-icon="true" data-show-trailing-icon="false" data-size="sm" data-state="Default" class="flex justify-center items-center gap-1.5 overflow-hidden">
              <div class="w-5 h-5 relative overflow-hidden">
                <svg :class="brandingSize < 2 ? 'fill-zinc-500' : 'fill-theme-green'" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_18731_11896)">
                    <path d="M18.3334 9.16667V2.5H12.5001V5H7.50008V2.5H1.66675V9.16667H7.50008V6.66667H9.16675V15H12.5001V17.5H18.3334V10.8333H12.5001V13.3333H10.8334V6.66667H12.5001V9.16667H18.3334ZM5.83341 7.5H3.33341V4.16667H5.83341V7.5ZM14.1667 12.5H16.6667V15.8333H14.1667V12.5ZM14.1667 4.16667H16.6667V7.5H14.1667V4.16667Z" />
                  </g>
                  <defs>
                    <clipPath id="clip0_18731_11896">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div :class="brandingSize < 2 ? 'text-zinc-500' : 'text-theme-green'" class="justify-start text-theme-green text-sm font-medium font-['Roboto'] leading-5">Manage</div>
            </button>
          </div>
          <div class="flex flex-col w-full gap-4 max-h-[300px] overflow-y-auto">
            <div
              v-for="location in locations"
              :key="location.id"
              class="self-stretch h-16 px-4 py-2 gap-[16px] rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex justify-start items-center"
            >
              <div class="w-6 h-6 relative overflow-hidden">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_18731_5934)">
                    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM7 9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9C17 11.88 14.12 16.19 12 18.88C9.92 16.21 7 11.85 7 9Z" fill="white" />
                    <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" fill="white" />
                  </g>
                  <defs>
                    <clipPath id="clip0_18731_5934">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div class="flex-1 inline-flex flex-col justify-start items-start">
                <div class="self-stretch justify-center text-white text-base font-normal font-['Roboto'] leading-6 line-clamp-1">{{ location.name || 'Unnamed location' }}</div>
                <div class="self-stretch justify-center text-neutral-400 text-xs font-normal font-['Roboto'] leading-4 line-clamp-1">{{ formatLocationAddress(location) }}</div>
              </div>
            </div>
          </div>
          <div v-if="locations.length === 0" class="self-stretch self-stretch p-6 h-[262px] rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-700 inline-flex flex-col justify-center items-center gap-3">
            <div class="w-6 h-6 relative overflow-hidden">
              <svg class="fill-zinc-500" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_18731_5934)">
                    <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM7 9C7 6.24 9.24 4 12 4C14.76 4 17 6.24 17 9C17 11.88 14.12 16.19 12 18.88C9.92 16.21 7 11.85 7 9Z"  />
                    <path d="M12 11.5C13.3807 11.5 14.5 10.3807 14.5 9C14.5 7.61929 13.3807 6.5 12 6.5C10.6193 6.5 9.5 7.61929 9.5 9C9.5 10.3807 10.6193 11.5 12 11.5Z" />
                  </g>
                  <defs>
                    <clipPath id="clip0_18731_5934">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
            </div>
            <div class="self-stretch text-center justify-center text-zinc-500 text-base font-normal font-['Roboto'] leading-6">No locations assigned</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Helpers from '@/helpers';
import { defineComponent } from "vue";
import {mapActions} from "vuex";
import { useSnackbar } from "vue3-snackbar";
import BrandingProfileApis from '@/controllers/BrandingProfileApis';
import SeoSetting from './SeoSetting.vue';

export default defineComponent({
    name: "EachProfile",
    components: {
      SeoSetting
    },
    setup() {
      const snackbar = useSnackbar();
      return { snackbar };
    },
    data() {
      return {
        locations: [],
      };
    },
    props: {
      brandingSize: {
        type: Number,
        default: 0
      },
      refetchAllLocationsKey: {
        type: Number,
        default: 0
      },
      branding: {
        type: {},
        default: null
      },
      businessName: {
        type: String,
        default: ''
      },
      merchantWebsiteUrl: {
        type: String,
        default: ''
      },
      googleAnalyticsTrackingId: {
        type: String,
        default: ''
      },
    },
    computed: {
      can_edit_business_brand_profiles() {
        return Helpers.getMyPermission('can_edit_business_brand_profiles')
      },
    },
    mounted() {
      if (this.branding?.id) this.fetchLocations();
    },
    watch: {
      refetchAllLocationsKey() {
        // update selected location info
        if (this.branding?.id) this.fetchLocations();
      },
    },
    methods: {
      formatLocationAddress(location) {
        const parts = [
          location.address1,
          location.address2,
          location.city,
          location.province,
          location.postalCode,
          location.country,
        ].filter(Boolean);
        return parts.length ? parts.join(', ') : 'No address';
      },
      async fetchLocations() {
        const brandingId = this.branding?.id;
        if (!brandingId) return;
        const res = await BrandingProfileApis.businessBrandingLocations(brandingId);
        const list = res?.data?.payload ?? [];
        const locationsList = Array.isArray(list) ? list : [];
        this.locations = locationsList;
      },
      showEditProfile() {
        this.$emit('showEditProfile')
      },
      showWarning(){
        this.confirmNew('WARNING', `Are you sure you want to delete that profile? You will have to reassign all locations under it to new ones for the online stores to function.`, 'Yes, Delete', 'bg-theme-red text-white').then(async () => {
          this.va_spinner(true);
          const brandingId = this.branding?.id;
          if (!brandingId) {
            this.snackbar.add({
              type: 'error',
              text: 'Profile ID not found',
              duration: 3000,
            });
            return;
          }
          const res = await BrandingProfileApis.removeBusinessBrandingProfile(brandingId);
          const isSuccess = res?.status === 200 || res?.status === 204;
          this.snackbar.add({
            type: isSuccess ? 'info' : 'error',
            text: isSuccess ? (res?.data?.message || 'Profile removed successfully') : (Helpers.catchErrorMessage(res?.response?.data) || 'Failed to remove profile'),
            duration: 3000,
          });
          this.va_spinner(false);
          if (isSuccess) {
            this.$emit('removed');
          }
        }).catch(() => {})
      },
      showManage() {
        this.$emit("showManage", this.branding)
      },
      
      ...mapActions({
        va_spinner: 'spinner/setSpinner',
      })
    }
  })
</script>